// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import type { AVPlaybackStatus, Video } from 'expo-av';
import { useCallback, useMemo, useRef, useState } from 'react';
import {
  Platform,
  StyleSheet,
  Text,
  View,
  type GestureResponderEvent,
  type LayoutChangeEvent,
  type StyleProp,
  type ViewStyle,
} from 'react-native';

import appColors, { alpha } from '@/theme/colors';

/**
 * How near a status frame has to be to the position we seeked to before it is believed to
 * be describing that seek rather than the moment before it. Wider than the 100ms between
 * frames a player asks for, so the first frame after the seek qualifies however it rounds.
 */
const SETTLED_TOLERANCE_MS = 400;

/**
 * How far off the asked-for position a seek may land while the finger is moving.
 *
 * An exact seek decodes forward from the previous keyframe to the precise frame; an
 * approximate one stops at the keyframe. Under a finger the keyframe is what you want —
 * it arrives at once, and the next drag position supersedes it anyway — so the picture
 * keeps up with the thumb instead of arriving in lurches. iOS honours this; Android and
 * web ignore it and seek their own way.
 */
const DRAG_SEEK_TOLERANCE_MS = 500;

/** m:ss — a flyover hole is well under an hour, so hours would only ever read "0:". */
export const formatClock = (millis: number): string => {
  const totalSeconds = Math.max(0, Math.floor(millis / 1000));
  return `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(2, '0')}`;
};

export type VideoScrub = {
  positionMs: number;
  durationMs: number;
  /** 0..1 — the finger's position while dragging, the video's otherwise. */
  progress: number;
  /** True while a finger owns the bar; the caller stops trusting status positions then. */
  scrubbing: boolean;
  handlers: {
    onStartShouldSetResponder: () => boolean;
    onResponderTerminationRequest: () => boolean;
    onResponderGrant: (event: GestureResponderEvent) => void;
    onResponderMove: (event: GestureResponderEvent) => void;
    onResponderRelease: () => void;
    onResponderTerminate: () => void;
  };
  onTrackLayout: (event: LayoutChangeEvent) => void;
  /** Feed every `onPlaybackStatusUpdate` frame through this. */
  applyStatus: (status: AVPlaybackStatus) => void;
  /** Back to the start of the bar — call when the source changes. */
  reset: () => void;
};

export type VideoScrubOptions = {
  /**
   * Called once when a finger takes the bar, after the video has been stopped.
   *
   * The stopping is the hook's — it holds the video, and taking the bar always stops the
   * picture. This is for the state around it that the caller owns: the play/pause button's
   * icon, an animation loop running beside the player. A caller that forgets it does not
   * get a video that plays on; it gets a button that lies about a stopped one.
   */
  onScrubStart?: () => void;
};

/**
 * Drag-to-seek for an expo-av `Video`.
 *
 * The state lives in a hook rather than inside the bar because the caller needs it too: a
 * status frame that arrives while a finger is down still describes the position from before
 * the seek landed, and applying it would drag the thumb backwards.
 */
export function useVideoScrub(
  videoRef: { current: Video | null },
  { onScrubStart }: VideoScrubOptions = {}
): VideoScrub {
  const [positionMs, setPositionMs] = useState(0);
  const [durationMs, setDurationMs] = useState(0);
  const [scrubFraction, setScrubFraction] = useState<number | null>(null);
  const trackWidthRef = useRef(0);
  const grantFractionRef = useRef(0);
  const grantPageXRef = useRef(0);
  const seekInFlightRef = useRef(false);
  const pendingSeekRef = useRef<number | null>(null);
  // The finger has let go but the last seek has not landed yet; the bar stays the truth
  // until it does, or a status frame from before it would drag the thumb back.
  const clearOnSettleRef = useRef(false);
  // Where the last seek asked the video to be, held from the moment it lands until a status
  // frame agrees. Frames issued before the seek describe the old position, and with the
  // video paused after a scrub none ever follows to correct one — the thumb would simply
  // stay wrong. Null once a frame has caught up, and status frames are trusted again.
  const settledTargetRef = useRef<number | null>(null);
  // A finger is on the bar, so more positions are coming and a seek need only be close.
  const fingerDownRef = useRef(false);
  // The last position asked for, so releasing can ask for it again exactly.
  const lastTargetRef = useRef<number | null>(null);
  // Held in a ref so a caller passing an inline arrow does not rebuild the handlers, and
  // through them the responder wiring, on every render.
  const onScrubStartRef = useRef(onScrubStart);
  onScrubStartRef.current = onScrubStart;

  /**
   * Move the picture with the finger, not on release.
   *
   * expo-av serves one seek at a time and a drag asks for far more than it can answer, so
   * a request that arrives mid-seek is held as the single pending one — the finger's latest
   * position wins and the positions it swept past are dropped rather than queued, which is
   * what keeps the video tracking the thumb instead of chasing it.
   *
   * A seek under the finger is allowed to land on the nearest keyframe rather than the exact
   * frame — see `DRAG_SEEK_TOLERANCE_MS`. `endScrub` then asks for the resting position
   * exactly, so what you let go on is the frame you get.
   */
  const seekVideoTo = useCallback(
    (target: number) => {
      const video = videoRef.current;
      lastTargetRef.current = target;
      if (!video) return;
      if (seekInFlightRef.current) {
        pendingSeekRef.current = target;
        return;
      }
      seekInFlightRef.current = true;
      const run = (ms: number) => {
        const tolerances = fingerDownRef.current
          ? {
              toleranceMillisBefore: DRAG_SEEK_TOLERANCE_MS,
              toleranceMillisAfter: DRAG_SEEK_TOLERANCE_MS,
            }
          : undefined;
        void Promise.resolve(video.setPositionAsync(ms, tolerances))
          .catch(() => undefined)
          .then(() => {
            const next = pendingSeekRef.current;
            pendingSeekRef.current = null;
            if (next === null) {
              seekInFlightRef.current = false;
              // The video is where it was asked to be; that is the position, until a status
              // frame says the same.
              settledTargetRef.current = ms;
              if (clearOnSettleRef.current) {
                clearOnSettleRef.current = false;
                setScrubFraction(null);
              }
              return;
            }
            run(next);
          });
      };
      run(target);
    },
    [videoRef]
  );

  const scrubToFraction = useCallback(
    (fraction: number) => {
      const clamped = Math.min(1, Math.max(0, fraction));
      setScrubFraction(clamped);
      if (durationMs <= 0) return;
      const target = clamped * durationMs;
      setPositionMs(target);
      seekVideoTo(target);
    },
    [durationMs, seekVideoTo]
  );

  /**
   * The finger is off the bar. Two things follow.
   *
   * The resting position is asked for once more, now that an exact seek costs nothing: the
   * seeks under the finger were allowed to stop at a keyframe, and the frame left on screen
   * should be the one the thumb and the distance above it are pointing at.
   *
   * And the thumb holds where it was left — the bar keeps owning the position until that
   * seek has actually landed, rather than handing back to a status frame that describes
   * where the video was before the drag.
   */
  const endScrub = useCallback(() => {
    fingerDownRef.current = false;
    if (lastTargetRef.current !== null) seekVideoTo(lastTargetRef.current);
    if (seekInFlightRef.current) {
      clearOnSettleRef.current = true;
      return;
    }
    setScrubFraction(null);
  }, [seekVideoTo]);

  const handlers = useMemo(
    () => ({
      onStartShouldSetResponder: () => true,
      // A scrub is a horizontal drag inside a screen that scrolls vertically; without this
      // the scroll view claims the gesture partway through and the thumb is left behind.
      onResponderTerminationRequest: () => false,
      onResponderGrant: (event: GestureResponderEvent) => {
        const width = trackWidthRef.current;
        grantPageXRef.current = event.nativeEvent.pageX;
        grantFractionRef.current =
          width > 0 ? Math.min(1, Math.max(0, event.nativeEvent.locationX / width)) : 0;
        fingerDownRef.current = true;
        // Stopped here, on the player itself, rather than left to the caller lowering a
        // `shouldPlay` prop: a scrub is someone looking for a frame, and the picture running
        // on under the finger carries them past it. Imperative because the prop route is a
        // render away and races the seeks this same gesture is firing.
        void Promise.resolve(videoRef.current?.pauseAsync?.()).catch(() => undefined);
        onScrubStartRef.current?.();
        scrubToFraction(grantFractionRef.current);
      },
      // Offset from where the finger landed, in page coordinates. `locationX` is measured
      // against whatever element the event is aimed at, which on web stops being the bar as
      // soon as the drag strays off it — and straying off it is the normal way to reach
      // either end. `pageX` is the same origin throughout.
      onResponderMove: (event: GestureResponderEvent) => {
        const width = trackWidthRef.current;
        if (width <= 0) return;
        const travelled = event.nativeEvent.pageX - grantPageXRef.current;
        scrubToFraction(grantFractionRef.current + travelled / width);
      },
      onResponderRelease: () => endScrub(),
      onResponderTerminate: () => endScrub(),
    }),
    [endScrub, scrubToFraction, videoRef]
  );

  const applyStatus = useCallback(
    (status: AVPlaybackStatus) => {
      if (!status.isLoaded) return;
      setDurationMs(status.durationMillis ?? 0);
      // A finger on the bar owns the position: status frames issued before the seek landed
      // still describe the old one and would drag the thumb back.
      if (scrubFraction !== null) return;

      const position = status.positionMillis ?? 0;
      const target = settledTargetRef.current;
      // Just after a seek the same is true without a finger: frames from before it are still
      // arriving. The seeked position stands until one of them agrees with it, which is
      // immediate while the video plays on and never — harmlessly — while it stays paused.
      if (target !== null) {
        if (Math.abs(position - target) > SETTLED_TOLERANCE_MS) return;
        settledTargetRef.current = null;
      }
      setPositionMs(position);
    },
    [scrubFraction]
  );

  const reset = useCallback(() => {
    setPositionMs(0);
    setDurationMs(0);
    setScrubFraction(null);
    // A new source starts believing its status frames again; holding the old video's
    // seeked position would ignore every frame the new one sends.
    clearOnSettleRef.current = false;
    settledTargetRef.current = null;
    lastTargetRef.current = null;
  }, []);

  const onTrackLayout = useCallback((event: LayoutChangeEvent) => {
    trackWidthRef.current = event.nativeEvent.layout.width;
  }, []);

  return {
    positionMs,
    durationMs,
    progress: scrubFraction ?? (durationMs > 0 ? Math.min(1, positionMs / durationMs) : 0),
    scrubbing: scrubFraction !== null,
    handlers,
    onTrackLayout,
    applyStatus,
    reset,
  };
}

export type VideoScrubBarProps = {
  scrub: VideoScrub;
  /** Spoken name of the bar; every caller has its own video to name. */
  accessibilityLabel: string;
  /** Prefixes every testID, so each player's bar is addressable on its own. */
  testIDPrefix: string;
  /**
   * The row takes its width from the parent, so a caller that puts the bar beside something
   * else has to say `flex: 1` — laid out as a row's child it otherwise shrinks to the two
   * clocks and the track between them collapses to nothing.
   */
  style?: StyleProp<ViewStyle>;
  /**
   * Rides above the thumb, saying what is at this point of the video — a flyover puts the
   * distance there. Omitted, the bar is the bar.
   */
  thumbLabel?: string | null;
  /** Colour of `thumbLabel`; a caller can say which of two things a number is. */
  thumbLabelColor?: string;
};

/** Elapsed · draggable track · duration. All the state is the hook's. */
export function VideoScrubBar({
  scrub,
  accessibilityLabel,
  testIDPrefix,
  style,
  thumbLabel,
  thumbLabelColor,
}: VideoScrubBarProps) {
  const percent = `${scrub.progress * 100}%` as const;
  return (
    <View style={[styles.scrubber, style]} testID={`${testIDPrefix}-scrubber`}>
      <Text style={styles.scrubberTime} testID={`${testIDPrefix}-elapsed`}>
        {formatClock(scrub.positionMs)}
      </Text>
      <View
        style={styles.trackTouchArea}
        testID={`${testIDPrefix}-scrubber-touch`}
        {...scrub.handlers}
      >
        <View
          style={styles.track}
          testID={`${testIDPrefix}-scrubber-track`}
          accessibilityRole="adjustable"
          accessibilityLabel={accessibilityLabel}
          accessibilityValue={{ min: 0, max: 100, now: Math.round(scrub.progress * 100) }}
          onLayout={scrub.onTrackLayout}
        >
          <View
            testID={`${testIDPrefix}-scrubber-fill`}
            style={[styles.trackFill, { width: percent }]}
          />
          <View style={[styles.trackThumb, { left: percent }]} />
          {thumbLabel ? (
            // Centred on the thumb by a half-width negative margin, the same trick the thumb
            // itself uses — a percentage `left` places the box's left edge, not its middle.
            // `pointerEvents` none so the label never intercepts the drag it is following.
            <View
              pointerEvents="none"
              testID={`${testIDPrefix}-thumb-label`}
              style={[styles.thumbLabel, { left: percent }]}
            >
              <Text
                numberOfLines={1}
                style={[styles.thumbLabelText, thumbLabelColor ? { color: thumbLabelColor } : null]}
              >
                {thumbLabel}
              </Text>
            </View>
          ) : null}
        </View>
      </View>
      <Text style={styles.scrubberTime} testID={`${testIDPrefix}-duration`}>
        {formatClock(scrub.durationMs)}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  scrubber: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    // A drag across the row is a scrub, not a text selection: without this the browser
    // highlights the clocks and hands the gesture to its own selection instead.
    ...Platform.select({ web: { userSelect: 'none' as const }, default: {} }),
  },
  scrubberTime: {
    // The clocks and the track sit on the panel, not on the picture, so they take the
    // skin's ink: white reads on classic's dark green and vanishes on a light skin.
    color: appColors.text.primary,
    fontSize: 12,
    fontVariant: ['tabular-nums'],
    // Both ends keep the bar still as the digits change; m:ss up to 9:59 fits.
    minWidth: 34,
    textAlign: 'center',
  },
  // The bar is 4px of picture but a 40px target: a thumb, not a mouse, is the usual
  // pointer here. No horizontal padding — locationX is read against this view, so its left
  // edge has to be the track's.
  trackTouchArea: { flex: 1, paddingVertical: 18, justifyContent: 'center' },
  track: { height: 4, borderRadius: 2, backgroundColor: alpha(appColors.text.primary, 0.25) },
  trackFill: { height: 4, borderRadius: 2, backgroundColor: appColors.accent.primary },
  // Sized to be grabbed with a thumb on a phone rather than pointed at with a cursor: the
  // 12px dot this replaces was a target you had to aim for. Centred on the 4px track, so
  // the offsets are (track - thumb) / 2 vertically and half the width horizontally.
  // Above the thumb, and wide enough for "123 m" without wrapping — a fixed width is what
  // lets the negative margin centre it on a moving thumb.
  thumbLabel: {
    position: 'absolute',
    bottom: 22,
    width: 76,
    marginLeft: -38,
    alignItems: 'center',
  },
  thumbLabelText: {
    color: appColors.text.onMedia,
    fontSize: 13,
    fontWeight: '700',
    fontVariant: ['tabular-nums'],
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 6,
    overflow: 'hidden',
    // A dark chip whatever the skin: the label's ink is the video's own — white counting
    // down to the green, yellow counting up from the tee — and both need dark behind them.
    backgroundColor: alpha(appColors.neutral.ink, 0.78),
  },
  trackThumb: {
    position: 'absolute',
    top: -13,
    width: 30,
    height: 30,
    borderRadius: 15,
    marginLeft: -15,
    backgroundColor: appColors.text.onMedia,
    // Against pale fairway — and against a light skin's panel — the white dot lost its
    // edge; the ring holds it apart. Near-black in every skin, so it always does.
    borderWidth: 2,
    borderColor: alpha(appColors.neutral.ink, 0.55),
  },
});
